import React from "react";
import {
  Box,
  ListItem,
  ListItemButton,
  Card,
  CardContent,
  Typography,
} from "@mui/material";
import {
  Folder as FolderIcon,
  InsertDriveFile as FileIcon,
  Image as ImageIcon,
  Description as DocumentIcon,
  Movie as VideoIcon,
  AudioFile as AudioIcon,
  Archive as ArchiveIcon,
  Code as CodeIcon,
  GridOn as SpreadsheetIcon,
} from "@mui/icons-material";
import RecycleBinItemActions from "./RecycleBinItemActions";
import { useTranslation } from "react-i18next";
import { formatFileSize, formatDate } from "../utils/fileOperations";

export const RecycleBinFileNameCell = ({ item, isSelected }) => {
  const type = item.type?.toLowerCase() || "";
  const getCellIcon = () => {
    if (item.isFolder) return <FolderIcon sx={{ fontSize: 24 }} />;
    if (["jpg", "jpeg", "png", "gif", "svg", "webp"].includes(type)) {
      return <ImageIcon sx={{ fontSize: 24 }} />;
    } else if (["doc", "docx", "pdf", "txt", "rtf"].includes(type)) {
      return <DocumentIcon sx={{ fontSize: 24 }} />;
    } else if (["mp4", "mov", "avi", "webm"].includes(type)) {
      return <VideoIcon sx={{ fontSize: 24 }} />;
    } else if (["mp3", "wav", "ogg", "flac"].includes(type)) {
      return <AudioIcon sx={{ fontSize: 24 }} />;
    } else if (["zip", "rar", "7z", "tar", "gz"].includes(type)) {
      return <ArchiveIcon sx={{ fontSize: 24 }} />;
    } else if (
      ["js", "html", "css", "py", "java", "php", "json"].includes(type)
    ) {
      return <CodeIcon sx={{ fontSize: 24 }} />;
    } else if (["xls", "xlsx", "csv"].includes(type)) {
      return <SpreadsheetIcon sx={{ fontSize: 24 }} />;
    } else {
      return <FileIcon sx={{ fontSize: 24 }} />;
    }
  };
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        color: isSelected ? "primary.main" : "text.primary",
      }}
    >
      {getCellIcon()}
      <Typography
        variant="body2"
        sx={{
          fontWeight: 500,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
        title={item.name}
      >
        {item.name}
      </Typography>
    </Box>
  );
};

const RecycleBinItem = ({
  item,
  user,
  viewType = "list",
  isSelected = false,
  onClick,
  divider = false,
  onActionComplete,
  ...props
}) => {
  const { t } = useTranslation();
  const translation = t("fileExplorer");

  if (!user || !user.slug) {
    return (
      <Box sx={{ p: 2, textAlign: "center" }}>
        <Typography variant="body2" color="text.secondary">
          User information not available
        </Typography>
      </Box>
    );
  }

  const getFileIcon = () => {
    if (item.isFolder) return <FolderIcon sx={{ fontSize: 24 }} />;
    const type = item.type?.toLowerCase() || "";
    if (["jpg", "jpeg", "png", "gif", "svg", "webp"].includes(type)) {
      return <ImageIcon sx={{ fontSize: 24 }} />;
    } else if (["doc", "docx", "pdf", "txt", "rtf"].includes(type)) {
      return <DocumentIcon sx={{ fontSize: 24 }} />;
    } else if (["mp4", "mov", "avi", "webm"].includes(type)) {
      return <VideoIcon sx={{ fontSize: 24 }} />;
    } else if (["mp3", "wav", "ogg", "flac"].includes(type)) {
      return <AudioIcon sx={{ fontSize: 24 }} />;
    } else if (["zip", "rar", "7z", "tar", "gz"].includes(type)) {
      return <ArchiveIcon sx={{ fontSize: 24 }} />;
    } else if (
      ["js", "html", "css", "py", "java", "php", "json"].includes(type)
    ) {
      return <CodeIcon sx={{ fontSize: 24 }} />;
    } else if (["xls", "xlsx", "csv"].includes(type)) {
      return <SpreadsheetIcon sx={{ fontSize: 24 }} />;
    } else {
      return <FileIcon sx={{ fontSize: 24 }} />;
    }
  };

  if (viewType === "grid") {
    return (
      <Card
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          cursor: "pointer",
          border: isSelected ? "2px solid #6264a7" : "1px solid #e0e0e0",
          borderRadius: 1,
          boxShadow: isSelected ? 3 : 1,
          "&:hover": { boxShadow: 3 },
        }}
        onClick={onClick}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            p: 2,
            backgroundColor: isSelected ? "rgba(98, 100, 167, 0.1)" : "#f5f5f5",
            height: 100,
          }}
        >
          {getFileIcon()}
        </Box>
        <CardContent sx={{ flexGrow: 1, p: 1 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={item.name}
          >
            {item.name}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: "block",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {formatFileSize(item.size)}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: "block",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {translation.deleted} {formatDate(item.deleted_at)}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <ListItem
      disablePadding
      divider={divider}
      sx={{
        backgroundColor: isSelected ? "rgba(98, 100, 167, 0.1)" : "transparent",
      }}
    >
      <ListItemButton
        onClick={onClick}
        sx={{
          py: 1,
          px: 2,
          "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
          <Box
            sx={{
              mr: 2,
              display: "flex",
              alignItems: "center",
              color: isSelected ? "primary.main" : "text.secondary",
            }}
          >
            {getFileIcon()}
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              flex: 1,
              minWidth: 0,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={item.name}
            >
              {item.name}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {formatFileSize(item.size)}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {translation.deleted} {formatDate(item.deleted_at)}
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              ml: 2,
              minWidth: "40px",
              visibility: "visible !important", // Force visibility
            }}
          >
            <RecycleBinItemActions
              item={item}
              user={user}
              onActionComplete={onActionComplete}
            />
          </Box>
        </Box>
      </ListItemButton>
    </ListItem>
  );
};

export default RecycleBinItem;
